import * as React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  DesignerLogo,
  ExplorerLogo,
  StudioLogo,
} from 'vet-bones/design-system/components';
import { isBrowserAuthConnection } from 'vet-bones/utils';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import { Loading } from 'src/ui/components/Loading';
import { ProductCard } from 'src/ui/components/ProductCard';
import { DASHBOARD_CARDS } from 'src/ui/constants/testIds';
import { QuickActions } from 'src/ui/containers/dashboard/QuickActions';
import { ServerOverview } from 'src/ui/containers/dashboard/ServerOverview';
import { Widgets } from 'src/ui/containers/dashboard/Widgets';
import {
  Connection,
  connectionInvalid,
  setConnectionBrowserAuthFailed,
  setConnectionDegraded,
  setConnectionOwnerEmail,
  setConnectionSSOError,
  setConnectionUnauthorized,
} from 'src/ui/features/connection';
import { DialogType, openedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection as GraphConnection,
  useGetConnectionByIndexQuery,
} from 'src/ui/graph/types';
import {
  getSSOTokenErrorCode,
  isConnectionTimeoutError,
  useProfile,
  useVerifyConnection,
} from 'src/ui/hooks/connection';
import { useIsOrganizationAdmin } from 'src/ui/hooks/organization';
import {
  isDegradedSelector,
  isUnauthorizedSelector,
} from 'src/ui/selectors/connection';
import { copy } from 'src/ui/templates/copy';
import { buildDashboardRoute } from 'src/ui/utils/buildRoute';
import {
  doDesignerRedirect,
  doExplorerRedirect,
  doStudioRedirect,
  redirectToUrl,
} from 'src/ui/utils/window';

interface DashboardProps {
  index: number;
}

export const Dashboard: React.VFC<DashboardProps> = ({ index }) => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();
  const isOrgAdmin = useIsOrganizationAdmin(org);

  const isDegraded = useAppSelector(isDegradedSelector);
  const isUnauthorized = useAppSelector(isUnauthorizedSelector);

  const { data: profile } = useProfile();
  const {
    data: connectionLookup,
    isLoading: isLoadingConnection,
  } = useGetConnectionByIndexQuery(
    graphQLClient,
    { index, org_domain: org },
    { refetchInterval: 10000 }
  );
  const { isLoading: isValidating, mutate: validate } = useVerifyConnection();
  const history = useHistory();
  const [current, setCurrent] = React.useState<GraphConnection>();
  const [isValidated, setIsValidated] = React.useState<boolean>(false);

  // This function is run every time the connection changes, for example when the
  // url is changed or when the initial connection information is resolved.
  React.useEffect(() => {
    if (isLoadingConnection || !connectionLookup) {
      return;
    }

    const { connection } = connectionLookup;

    if (!connection) {
      history.push(buildDashboardRoute(org));
      return;
    }

    if (connection.isAllocating) {
      return;
    }

    if (!current || connection.id !== current.id) {
      setCurrent(connection);
      setIsValidated(false);
      dispatch(setConnectionBrowserAuthFailed(false));
      dispatch(setConnectionUnauthorized(false));
      dispatch(setConnectionDegraded(false));
      dispatch(setConnectionSSOError(null));
      if (connection.cloud?.owner?.email) {
        dispatch(setConnectionOwnerEmail(connection.cloud?.owner?.email));
      }
    }
  }, [connectionLookup, isLoadingConnection, current, dispatch, history, org]);

  React.useEffect(() => {
    if (
      !current ||
      current?.isAllocating ||
      isValidated ||
      isDegraded ||
      isUnauthorized
    ) {
      return;
    }

    const isUsingStardogToken = Boolean(!current?.useSSO && current?.token);

    validate(current as Connection, {
      onSuccess: () => setIsValidated(true),
      onError: (error) => {
        if (isConnectionTimeoutError(error)) {
          dispatch(setConnectionUnauthorized(true));
          return;
        }

        const ssoCode = getSSOTokenErrorCode(error);
        if (ssoCode) {
          dispatch(setConnectionSSOError(ssoCode));
          return;
        }

        // If they are using Stardog token it has expired and they need
        // re-enter their password to get a new token.
        if (isUsingStardogToken) {
          dispatch(connectionInvalid(index));
          dispatch(openedDialog({ type: DialogType.SIGN_IN }));
          return;
        }

        if (current.useSSO) {
          // If they are using sso, then there they are most likely not
          // authorized or some other error with communication
          dispatch(setConnectionUnauthorized(true));
        } else if (isBrowserAuthConnection(current)) {
          // If useBrowserAuth was enabled and validate connection failed,
          // show banner warning for this
          dispatch(setConnectionBrowserAuthFailed(true));
        } else {
          // If they are not using sso and don't have a token, then Stardog
          // does not support tokens (most likely) show configuration helper
          dispatch(setConnectionDegraded(true));
        }
      },
    });
  }, [
    isDegraded,
    isValidated,
    isUnauthorized,
    current,
    validate,
    dispatch,
    index,
  ]);

  if (
    isLoadingConnection ||
    !connectionLookup ||
    !connectionLookup.connection
  ) {
    return <Loading />;
  }

  const { connection } = connectionLookup;

  if (connection.isAllocating) {
    if (connection.stripeSubscription?.isWaitingForPayment) {
      redirectToUrl(connection.stripeSubscription?.url || '/');
    }
    return (
      <div className="sd-container">
        <p>Please wait while your instance is being built.</p>
      </div>
    );
  }

  const goToStudio = () => {
    return isUnauthorized ? null : doStudioRedirect(org, Number(index));
  };

  const goToExplorer = () => {
    return isUnauthorized ? null : doExplorerRedirect(org, Number(index));
  };

  const goToDesigner = () => {
    return isUnauthorized ? null : doDesignerRedirect(org, Number(index));
  };

  const isMyEndpoint =
    connection.organization?.owner &&
    connection.organization?.owner?.username === profile?.profile?.username;
  const isStardogCloudOrFree =
    connection.isStardogCloud || connection.isStardogFree;

  return isValidating ? (
    <Loading />
  ) : (
    <div className="dashboard">
      <div className="dashboard-cards" data-testid={DASHBOARD_CARDS}>
        <div className="dashboard-apps">
          <ProductCard
            description={copy.dashboard.designer.description}
            icon={DesignerLogo}
            label={copy.dashboard.designer.label}
            onClick={goToDesigner}
          />
          <ProductCard
            description={copy.dashboard.explorer.description}
            icon={ExplorerLogo}
            label={copy.dashboard.explorer.label}
            onClick={goToExplorer}
          />
          <ProductCard
            description={copy.dashboard.studio.description}
            icon={StudioLogo}
            label={copy.dashboard.studio.label}
            onClick={goToStudio}
          />
        </div>
        <div className="dashboard-panels">
          <ServerOverview connection={connection} />
          <QuickActions
            index={index}
            org={org}
            showManageEndpointUsers={
              org ? isOrgAdmin : Boolean(isMyEndpoint && isStardogCloudOrFree)
            }
            showManageDatabricks={!org}
            showManageEndpoints={!org || isOrgAdmin}
          />
        </div>
        <div className="dashboard-widgets">
          <Widgets />
        </div>
      </div>
    </div>
  );
};
